import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BrandLogo } from '../components/BrandLogo';
import { ArrowLeft, Gift, Users, CreditCard, Share2, Copy, MessageCircle, Mail, CheckCircle, ArrowRight, Shield, Download, Loader2 } from 'lucide-react';

const LOGO_URL = "https://customer-assets-agu9un31.emergentagent.net/job_media-stage-25/artifacts/qiil6l84_brand%20stage%20logo.png";

function usePdfDownload() {
  const [generating, setGenerating] = useState(false);
  const contentRef = useRef(null);

  const downloadPdf = async (filename) => {
    if (!contentRef.current || generating) return;
    setGenerating(true);
    try {
      const html2pdf = (await import('html2pdf.js')).default;
      const el = contentRef.current;

      // Temporarily apply print-friendly styles
      el.classList.add('pdf-render-mode');

      await html2pdf().set({
        margin: [12, 14, 12, 14],
        filename: filename,
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: '#ffffff' },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
      }).from(el).save();

      el.classList.remove('pdf-render-mode');
    } catch (err) {
      console.error('PDF generation failed:', err);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  return { contentRef, generating, downloadPdf };
}


// ─── Inline print-friendly CSS ──────────────────────────────────────
const pdfStyles = `
  .pdf-render-mode {
    background: #ffffff !important;
    color: #1a1a1a !important;
    padding: 24px !important;
  }
  .pdf-render-mode * {
    color: #1a1a1a !important;
    border-color: #d4d4d8 !important;
  }
  .pdf-render-mode .pdf-brand-header {
    display: flex !important;
  }
  .pdf-render-mode h1, .pdf-render-mode h2, .pdf-render-mode h3 {
    color: #18181b !important;
  }
  .pdf-render-mode .pdf-accent {
    color: #C41E3A !important;
  }
  .pdf-render-mode .pdf-amber-accent {
    color: #d97706 !important;
  }
  .pdf-render-mode .pdf-section {
    background: #f4f4f5 !important;
    border-color: #d4d4d8 !important;
  }
  .pdf-render-mode .pdf-highlight-section {
    background: #fef3c7 !important;
    border-color: #f59e0b !important;
  }
  .pdf-render-mode .pdf-green-section {
    background: #ecfdf5 !important;
    border-color: #10b981 !important;
  }
  .pdf-render-mode .pdf-green-section * {
    color: #065f46 !important;
  }
  .pdf-render-mode .pdf-card {
    background: #f9fafb !important;
    border: 1px solid #d4d4d8 !important;
  }
  .pdf-render-mode .pdf-table thead {
    background: #f4f4f5 !important;
  }
  .pdf-render-mode .pdf-table th {
    color: #52525b !important;
  }
  .pdf-render-mode .pdf-hide {
    display: none !important;
  }
  .pdf-render-mode .pdf-step-circle {
    background: #fee2e2 !important;
    border-color: #C41E3A !important;
  }
  .pdf-render-mode .pdf-step-circle span {
    color: #C41E3A !important;
  }
  .pdf-render-mode .pdf-amber-step-circle {
    background: #fef3c7 !important;
    border-color: #d97706 !important;
  }
  .pdf-render-mode .pdf-amber-step-circle span {
    color: #d97706 !important;
  }
  .pdf-render-mode .pdf-footer {
    margin-top: 24px;
    padding-top: 16px;
    border-top: 1px solid #d4d4d8;
    text-align: center;
    font-size: 11px;
    color: #71717a !important;
  }
  .pdf-render-mode .pdf-footer * {
    color: #71717a !important;
  }
`;


export function ReferralGuidePage() {
  const navigate = useNavigate();
  const { contentRef, generating, downloadPdf } = usePdfDownload();

  return (
    <div className="min-h-screen bg-zinc-950">
      <style>{pdfStyles}</style>
      <header className="fixed top-0 left-0 right-0 z-50 bg-zinc-950/90 backdrop-blur-md border-b border-zinc-800">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to Home</span>
          </button>
          <BrandLogo className="h-10" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 md:px-8 pt-24 pb-16">
        {/* Download PDF button */}
        <div className="flex justify-end mb-4">
          <button
            onClick={() => downloadPdf('BrandStage_Referral_Programme.pdf')}
            disabled={generating}
            className="flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors border border-zinc-700 disabled:opacity-50"
            data-testid="download-referral-pdf-btn"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            {generating ? 'Generating PDF...' : 'Download PDF'}
          </button>
        </div>

        {/* ── PDF-captured content starts here ── */}
        <div ref={contentRef}>
          {/* Brand header - visible only in PDF */}
          <div className="pdf-brand-header hidden items-center gap-3 mb-6 pb-4" style={{ borderBottom: '2px solid #C41E3A' }}>
            <img src={LOGO_URL} alt="BrandStage" style={{ height: '36px' }} crossOrigin="anonymous" />
            <div style={{ marginLeft: 'auto', fontSize: '11px', color: '#71717a', textAlign: 'right' }}>
              <span>brandstage-media.com</span><br />
              <span>operations@brandstage-media.com</span>
            </div>
          </div>

          {/* Hero */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-[#C41E3A]/10 text-[#C41E3A] px-4 py-1.5 rounded-full text-sm font-medium mb-4 pdf-accent">
              <Gift className="w-4 h-4" />
              Referral Programme
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4" data-testid="referral-guide-title">
              Share BrandStage. Earn Rewards.
            </h1>
            <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
              Know a business that deserves to be better understood? Refer them to BrandStage and
              earn credits every time they subscribe.
            </p>
          </div>

          {/* How It Works */}
          <div className="border border-[#C41E3A]/20 rounded-2xl bg-zinc-900/30 p-6 md:p-10 mb-10 pdf-section">
            <h2 className="text-xl font-bold text-white mb-8 text-center">How It Works</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { step: '1', title: 'Share Your Link', desc: 'Find your unique referral link on your Dashboard. Share it via WhatsApp, email, or simply copy and send it to anyone you think would benefit from BrandStage.' },
                { step: '2', title: 'They Subscribe', desc: 'When someone signs up using your link and subscribes to any BrandStage plan, both of you are rewarded. No extra steps for either of you.' },
                { step: '3', title: 'You Earn Credits', desc: 'BrandStage Credits are automatically added to your account. They are applied towards your next renewal or upgrade — reducing what you pay.' },
              ].map(s => (
                <div key={s.step} className="text-center">
                  <div className="pdf-step-circle w-12 h-12 rounded-full bg-[#C41E3A]/10 border border-[#C41E3A]/30 flex items-center justify-center mx-auto mb-4">
                    <span className="text-[#C41E3A] font-bold text-lg pdf-accent">{s.step}</span>
                  </div>
                  <h3 className="text-white font-semibold mb-2">{s.title}</h3>
                  <p className="text-zinc-400 text-sm leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Reward Tiers */}
          <div className="mb-10">
            <h2 className="text-xl font-bold text-white mb-6 text-center">What You Earn</h2>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 text-center pdf-card">
                <p className="text-zinc-400 text-sm mb-2">Your referral subscribes to</p>
                <p className="text-white font-semibold mb-3">6-Month Plan</p>
                <p className="text-3xl font-bold text-white">₹250</p>
                <p className="text-zinc-500 text-xs mt-2">credited to your account</p>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 text-center pdf-card">
                <p className="text-zinc-400 text-sm mb-2">Your referral subscribes to</p>
                <p className="text-white font-semibold mb-3">1-Year Plan</p>
                <p className="text-3xl font-bold text-white">₹500</p>
                <p className="text-zinc-500 text-xs mt-2">credited to your account</p>
              </div>
              <div className="bg-zinc-900 border border-[#C41E3A]/30 rounded-xl p-6 text-center relative overflow-hidden pdf-card">
                <div className="absolute top-0 right-0 bg-[#C41E3A] text-white text-[10px] font-bold px-3 py-1 rounded-bl-lg">BEST</div>
                <p className="text-zinc-400 text-sm mb-2">Your referral subscribes to</p>
                <p className="text-white font-semibold mb-3">All-Access Plan</p>
                <p className="text-3xl font-bold text-[#C41E3A] pdf-accent">₹1,000</p>
                <p className="text-zinc-500 text-xs mt-2">credited to your account</p>
              </div>
            </div>
          </div>

          {/* Your referral benefits too */}
          <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-6 mb-10 flex items-start gap-4 pdf-green-section">
            <CheckCircle className="w-6 h-6 text-emerald-400 shrink-0 mt-0.5" />
            <div>
              <h3 className="text-white font-semibold mb-1">Your referral benefits too</h3>
              <p className="text-zinc-300 text-sm leading-relaxed">
                The person you refer receives <strong className="text-white">₹250 off</strong> their first subscription as a welcome benefit.
                It's applied automatically at checkout — no coupon codes needed.
              </p>
            </div>
          </div>

          {/* Key Details */}
          <div className="border border-zinc-800 rounded-2xl bg-zinc-900/30 p-6 md:p-8 mb-10 pdf-section">
            <h2 className="text-xl font-bold text-white mb-6">Key Details</h2>
            <div className="space-y-4 text-sm">
              {[
                { q: 'Where do I find my referral link?', a: 'Log in to your Dashboard. The "Refer & Earn" card at the top shows your unique link with one-tap sharing to WhatsApp, email, or clipboard.' },
                { q: 'When are credits added?', a: 'Credits are added to your account instantly when the person you referred makes their first successful subscription payment.' },
                { q: 'How are credits used?', a: 'Credits are automatically applied to your next subscription renewal or upgrade. You\'ll see the credit deduction clearly in your checkout summary.' },
                { q: 'Is there a limit?', a: 'There is no limit on how many people you can refer. Credits stack with every successful referral. If your balance exceeds ₹15,000, the overflow carries to your next subscription cycle.' },
                { q: 'Do credits expire?', a: 'Credits expire at the end of your current subscription period. We recommend using them before renewal.' },
                { q: 'Can I refer someone who already has an account?', a: 'The referral link works for new sign-ups only. If someone already has a BrandStage account, they won\'t be tracked as your referral.' },
              ].map((item, i) => (
                <div key={i} className="border-b border-zinc-800 pb-4 last:border-0 last:pb-0">
                  <p className="text-white font-medium mb-1">{item.q}</p>
                  <p className="text-zinc-400 leading-relaxed">{item.a}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="text-center pdf-hide">
            <h3 className="text-xl font-bold text-white mb-3">Ready to start referring?</h3>
            <p className="text-zinc-400 mb-6">Log in to your dashboard to get your unique referral link.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button onClick={() => navigate('/dashboard')} className="bg-[#C41E3A] hover:bg-[#A01830] text-white px-6 py-3 rounded-xl font-medium transition-colors flex items-center gap-2" data-testid="go-to-dashboard-btn">
                Go to Dashboard <ArrowRight className="w-4 h-4" />
              </button>
              <button onClick={() => navigate('/partner-programme')} className="bg-zinc-800 hover:bg-zinc-700 text-white px-6 py-3 rounded-xl font-medium transition-colors border border-zinc-700" data-testid="partner-programme-btn">
                Partner Programme
              </button>
            </div>
          </div>

          {/* PDF-only CTA */}
          <div className="hidden text-center mt-6 pdf-footer">
            <p><strong>Ready to start referring?</strong></p>
            <p>Visit <strong style={{ color: '#C41E3A' }}>brandstage-media.com</strong> → Log in → Dashboard → Refer & Earn</p>
            <p style={{ marginTop: '8px' }}>Questions? Email operations@brandstage-media.com or WhatsApp +91 9940067960</p>
            <p style={{ marginTop: '12px' }}>&copy; {new Date().getFullYear()} BrandStage Media. A Continex Initiative.</p>
          </div>
        </div>
        {/* ── PDF-captured content ends ── */}
      </main>

      <footer className="border-t border-zinc-800 py-8">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-zinc-500 text-sm">&copy; {new Date().getFullYear()} BrandStage. Powered by CONTINEX.</p>
        </div>
      </footer>
    </div>
  );
}


export function PartnerProgrammePage() {
  const navigate = useNavigate();
  const { contentRef, generating, downloadPdf } = usePdfDownload();

  return (
    <div className="min-h-screen bg-zinc-950">
      <style>{pdfStyles}</style>
      <header className="fixed top-0 left-0 right-0 z-50 bg-zinc-950/90 backdrop-blur-md border-b border-zinc-800">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to Home</span>
          </button>
          <BrandLogo className="h-10" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 md:px-8 pt-24 pb-16">
        {/* Download PDF button */}
        <div className="flex justify-end mb-4">
          <button
            onClick={() => downloadPdf('BrandStage_Partner_Programme.pdf')}
            disabled={generating}
            className="flex items-center gap-2 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 px-4 py-2 rounded-lg text-sm font-medium transition-colors border border-amber-500/30 disabled:opacity-50"
            data-testid="download-partner-pdf-btn"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            {generating ? 'Generating PDF...' : 'Download PDF Guide'}
          </button>
        </div>

        {/* ── PDF-captured content starts here ── */}
        <div ref={contentRef}>
          {/* Brand header - visible only in PDF */}
          <div className="pdf-brand-header hidden items-center gap-3 mb-6 pb-4" style={{ borderBottom: '2px solid #C41E3A' }}>
            <img src={LOGO_URL} alt="BrandStage" style={{ height: '36px' }} crossOrigin="anonymous" />
            <div style={{ marginLeft: 'auto', fontSize: '11px', color: '#71717a', textAlign: 'right' }}>
              <span>brandstage-media.com</span><br />
              <span>operations@brandstage-media.com</span>
            </div>
          </div>

          {/* Hero */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-amber-500/10 text-amber-400 px-4 py-1.5 rounded-full text-sm font-medium mb-4 pdf-amber-accent">
              <Users className="w-4 h-4" />
              Partner Programme
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4" data-testid="partner-guide-title">
              BrandStage Partner Consultant Programme
            </h1>
            <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
              For consultants, trade advisors, and business networks who recommend BrandStage to
              multiple organisations. Higher rewards, dedicated tracking, and cash payout options.
            </p>
          </div>

          {/* Why Partner */}
          <div className="border border-amber-500/20 rounded-2xl bg-zinc-900/30 p-6 md:p-10 mb-10 pdf-highlight-section">
            <h2 className="text-xl font-bold text-white mb-3">Why become a BrandStage Partner?</h2>
            <p className="text-zinc-300 leading-relaxed mb-6">
              As a trade consultant, business advisor, or industry network operator, you regularly engage
              with businesses that could benefit from stronger digital visibility. BrandStage's Partner
              Programme lets you recommend a platform you believe in — and earn meaningful rewards for
              every successful introduction.
            </p>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-zinc-800/50 rounded-lg p-4 text-center pdf-card">
                <p className="text-2xl font-bold text-amber-400 mb-1 pdf-amber-accent">₹2,000</p>
                <p className="text-zinc-400 text-sm">per annual subscription</p>
              </div>
              <div className="bg-zinc-800/50 rounded-lg p-4 text-center pdf-card">
                <p className="text-2xl font-bold text-white mb-1">Unlimited</p>
                <p className="text-zinc-400 text-sm">referrals, no cap on contacts</p>
              </div>
              <div className="bg-zinc-800/50 rounded-lg p-4 text-center pdf-card">
                <p className="text-2xl font-bold text-white mb-1">Cash Option</p>
                <p className="text-zinc-400 text-sm">for high-volume partners</p>
              </div>
            </div>
          </div>

          {/* Reward Tiers */}
          <div className="mb-10">
            <h2 className="text-xl font-bold text-white mb-6">Partner Reward Structure</h2>
            <div className="overflow-hidden border border-zinc-800 rounded-xl">
              <table className="w-full text-sm pdf-table">
                <thead className="bg-zinc-900">
                  <tr className="text-zinc-400">
                    <th className="px-5 py-3 text-left">Referred Contact Subscribes To</th>
                    <th className="px-5 py-3 text-right">Regular Referral</th>
                    <th className="px-5 py-3 text-right text-amber-400 pdf-amber-accent">Partner Reward</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800">
                  <tr className="text-zinc-300">
                    <td className="px-5 py-4">6-Month Basic Plan</td>
                    <td className="px-5 py-4 text-right">₹250</td>
                    <td className="px-5 py-4 text-right font-semibold text-amber-400 pdf-amber-accent">₹250</td>
                  </tr>
                  <tr className="text-zinc-300">
                    <td className="px-5 py-4">1-Year Basic Plan</td>
                    <td className="px-5 py-4 text-right">₹500</td>
                    <td className="px-5 py-4 text-right font-semibold text-amber-400 pdf-amber-accent">₹2,000</td>
                  </tr>
                  <tr className="text-zinc-300 bg-zinc-900/30">
                    <td className="px-5 py-4">All-Access (Annual)</td>
                    <td className="px-5 py-4 text-right">₹1,000</td>
                    <td className="px-5 py-4 text-right font-semibold text-amber-400 pdf-amber-accent">₹2,000</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-zinc-500 text-xs mt-3">
              Custom reward tiers may be discussed for strategic partners with high referral volumes.
            </p>
          </div>

          {/* How It Works for Partners */}
          <div className="border border-zinc-800 rounded-2xl bg-zinc-900/30 p-6 md:p-8 mb-10 pdf-section">
            <h2 className="text-xl font-bold text-white mb-6">How It Works</h2>
            <div className="space-y-6">
              {[
                { num: '1', title: 'Get Onboarded', desc: 'Contact BrandStage to express your interest. Our team will activate your Partner status on the platform. There is no joining fee or commitment required.' },
                { num: '2', title: 'Receive Your Referral Link', desc: 'Once activated, you\'ll find your unique referral link on your Dashboard under the "Refer & Earn" section. This same link is used for all your referrals — no need for separate codes per contact.' },
                { num: '3', title: 'Share with Your Network', desc: 'Share the link with businesses in your network via WhatsApp, email, during meetings, or in your newsletters. When a contact clicks your link and signs up, the referral is automatically tracked.' },
                { num: '4', title: 'Track Your Referrals', desc: 'Your Partner Dashboard shows every referral: contact name, email, subscription status, credits earned, and date. You can download a CSV report of all activity at any time.' },
                { num: '5', title: 'Earn Rewards', desc: 'Credits are added to your account when a referred contact subscribes. Credits auto-apply to your next BrandStage subscription renewal. For high-volume partners, we offer cash payout options — speak with our team to arrange this.' },
              ].map(s => (
                <div key={s.num} className="flex gap-4">
                  <div className="pdf-amber-step-circle w-8 h-8 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center shrink-0 mt-0.5">
                    <span className="text-amber-400 font-bold text-sm pdf-amber-accent">{s.num}</span>
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">{s.title}</h3>
                    <p className="text-zinc-400 text-sm leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* What Your Contacts Get */}
          <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-6 mb-10 pdf-green-section">
            <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-400" />
              What Your Contacts Receive
            </h3>
            <ul className="space-y-2 text-sm text-zinc-300">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                <span><strong className="text-white">₹250 off</strong> their first BrandStage subscription — applied automatically at checkout.</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                <span>A <strong className="text-white">private, link-only</strong> professional profile — not indexed by search engines.</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                <span>AI-assisted profile creation, video hosting, email signatures, QR codes, and more.</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                <span>Full control over who sees their profile — they decide who gets the link.</span>
              </li>
            </ul>
          </div>

          {/* Cash Payout */}
          <div className="border border-zinc-800 rounded-2xl bg-zinc-900/30 p-6 md:p-8 mb-10 pdf-section">
            <h2 className="text-xl font-bold text-white mb-4">Cash Payout Option</h2>
            <p className="text-zinc-300 text-sm leading-relaxed mb-4">
              For partners who refer a significant number of contacts, we offer a <strong className="text-white">cash payout</strong> option
              as an alternative to platform credits. Here's how it works:
            </p>
            <div className="space-y-3 text-sm">
              {[
                'Credits accumulate as referrals convert. You can track your balance on your Dashboard at all times.',
                'Once your accumulated credits reach ₹5,000 or more, you can request a cash payout by contacting our team.',
                'Cash payouts are processed via bank transfer (NEFT/IMPS) within 7 working days of the request. Applicable TDS will be deducted as per Indian tax regulations.',
                'For payouts, we will require your PAN and bank account details (shared securely through our platform).',
              ].map((text, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-zinc-800 flex items-center justify-center shrink-0 mt-0.5 pdf-card">
                    <span className="text-zinc-400 text-xs font-bold">{i + 1}</span>
                  </div>
                  <p className="text-zinc-300">{text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* FAQ */}
          <div className="border border-zinc-800 rounded-2xl bg-zinc-900/30 p-6 md:p-8 mb-10 pdf-section">
            <h2 className="text-xl font-bold text-white mb-6">Frequently Asked Questions</h2>
            <div className="space-y-4 text-sm">
              {[
                { q: 'Is there a cost to become a Partner?', a: 'No. There is no joining fee, subscription requirement, or commitment. You simply need a BrandStage account and Partner status activated by our team.' },
                { q: 'Do I need to be a BrandStage subscriber myself?', a: 'No. You can be a Partner without having your own BrandStage profile subscription. However, if you do subscribe, your credits can be applied to your own renewals.' },
                { q: 'Can I choose between credits and cash?', a: 'Yes. Credits are the default and are applied automatically. Cash payouts are available for balances of ₹5,000 or more — contact our team to arrange this.' },
                { q: 'Is there a limit on referrals?', a: 'No. You can refer as many contacts as you like. Credits stack with every successful referral. If your credit balance exceeds ₹15,000 in a cycle, the overflow carries to the next cycle.' },
                { q: 'How do I know when a referral converts?', a: 'Your Partner Dashboard shows real-time status of every referral — including pending sign-ups, active subscriptions, and credits earned. You can also download a CSV report.' },
                { q: 'What if I want custom reward rates?', a: 'For strategic partners with high referral volumes, we\'re open to discussing custom reward structures. Contact us at operations@brandstage-media.com.' },
                { q: 'What does the referred contact need to do?', a: 'Nothing special. They click your link, sign up, and subscribe. The referral is tracked automatically. They also receive ₹250 off their first subscription.' },
              ].map((item, i) => (
                <div key={i} className="border-b border-zinc-800 pb-4 last:border-0 last:pb-0">
                  <p className="text-white font-medium mb-1">{item.q}</p>
                  <p className="text-zinc-400 leading-relaxed">{item.a}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Contact CTA - on screen only */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8 text-center pdf-hide">
            <h3 className="text-xl font-bold text-white mb-3">Interested in becoming a Partner?</h3>
            <p className="text-zinc-400 mb-6 max-w-lg mx-auto">
              Reach out to our team to get your Partner status activated. It takes less than a day.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="mailto:operations@brandstage-media.com?subject=BrandStage Partner Programme Enquiry"
                className="bg-[#C41E3A] hover:bg-[#A01830] text-white px-6 py-3 rounded-xl font-medium transition-colors flex items-center gap-2"
                data-testid="partner-enquiry-btn"
              >
                <Mail className="w-4 h-4" /> Email Us
              </a>
              <a
                href="https://wa.me/919940067960?text=Hi%2C%20I%27m%20interested%20in%20the%20BrandStage%20Partner%20Programme."
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#25D366]/20 hover:bg-[#25D366]/30 text-[#25D366] px-6 py-3 rounded-xl font-medium transition-colors flex items-center gap-2 border border-[#25D366]/30"
                data-testid="partner-whatsapp-btn"
              >
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
              <button
                onClick={() => navigate('/referral-programme')}
                className="bg-zinc-800 hover:bg-zinc-700 text-white px-6 py-3 rounded-xl font-medium transition-colors border border-zinc-700"
                data-testid="regular-referral-btn"
              >
                Regular Referral Programme
              </button>
            </div>
          </div>

          {/* PDF-only footer with contact info */}
          <div className="hidden pdf-footer" style={{ marginTop: '24px' }}>
            <p><strong>Interested in becoming a BrandStage Partner?</strong></p>
            <p>Email: <strong>operations@brandstage-media.com</strong> &nbsp;|&nbsp; WhatsApp: <strong>+91 9940067960</strong></p>
            <p>Visit: <strong style={{ color: '#C41E3A' }}>brandstage-media.com/partner-programme</strong></p>
            <p style={{ marginTop: '12px' }}>&copy; {new Date().getFullYear()} BrandStage Media. A Continex Initiative. All rights reserved.</p>
          </div>
        </div>
        {/* ── PDF-captured content ends ── */}
      </main>

      <footer className="border-t border-zinc-800 py-8">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-zinc-500 text-sm">&copy; {new Date().getFullYear()} BrandStage. Powered by CONTINEX.</p>
        </div>
      </footer>
    </div>
  );
}
